import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Star, Shield, Trophy, Upload, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { unifiedAPI } from '../../utils/api';
import QuickOrderModal from '../../components/QuickOrderModal';

const AwardsHome = () => {
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuickOrder, setShowQuickOrder] = useState(false);

  // Fetch categories and featured products from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch categories and featured products in parallel
        const [categoriesRes, productsRes] = await Promise.all([
          unifiedAPI.getCategories({ product_type: 'awards', include_counts: true }),
          unifiedAPI.getProducts({ product_type: 'awards', limit: 8, sort: 'name_asc' })
        ]);
        
        setCategories(categoriesRes.categories || []);
        setFeaturedProducts(productsRes.products || []);
      } catch (error) {
        console.error('Error fetching awards data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-ivory-50" data-testid="awards-home">
      {/* Hero Section - Elegant champagne gradient */}
      <section className="relative gradient-hero py-24 overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold-500/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gold-400/5 rounded-full blur-[100px]" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-4 py-2 bg-gold-500/10 border border-gold-500/20 rounded-full text-gold-700 text-sm font-medium font-body mb-6">
              Premium Recognition Awards
            </span>
            <h1 className="text-5xl md:text-6xl font-heading font-semibold text-ivory-800 mb-6">
              Crystal <span className="gradient-text-gold">Awards</span>
            </h1>
            <p className="text-xl text-ivory-500 mb-8 leading-relaxed font-body">
              Celebrate excellence with our stunning collection of crystal awards. 
              Each piece is meticulously crafted and laser engraved to honor your achievements.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg" 
                className="btn-luxury-primary px-8 py-6 text-lg rounded-lg font-body"
                asChild
              >
                <Link to="/Awards/products" data-testid="browse-awards-btn">
                  Browse Collection
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="btn-luxury-outline px-8 py-6 text-lg rounded-lg font-body"
                onClick={() => setShowQuickOrder(true)}
                data-testid="quick-order-btn"
              >
                <Upload className="mr-2 w-5 h-5" />
                Quick Order
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Subtle stone gradient */}
      <section className="py-16 gradient-stone">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: Award, title: 'Premium Quality', desc: 'Optical crystal with exceptional clarity' },
              { icon: Star, title: 'Custom Engraving', desc: 'Laser etched personalization included' },
              { icon: Shield, title: 'Gift Packaging', desc: 'Elegant presentation boxes' },
              { icon: Trophy, title: 'Fast Turnaround', desc: '3-5 business days production' }
            ].map((feature, idx) => (
              <Card key={idx} className="bg-ivory-100 border border-ivory-300/50 shadow-luxury">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 bg-gold-500/10 rounded-lg flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-gold-600" />
                  </div>
                  <h3 className="font-heading font-semibold text-ivory-800 mb-2">{feature.title}</h3>
                  <p className="text-sm text-ivory-500 font-body">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-ivory-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-semibold text-ivory-800 mb-4">Shop by Category</h2>
            <p className="text-ivory-500 max-w-2xl mx-auto font-body">
              Explore our curated collection of recognition awards for every occasion
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-10 h-10 text-gold-500 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/Awards/products/${category.id}`}
                  className="group"
                  data-testid={`category-card-${category.id}`}
                >
                  <Card className="bg-ivory-100 border border-ivory-300/50 hover:border-gold-500/50 transition-all duration-300 overflow-hidden h-full card-luxury">
                    <div className="aspect-square overflow-hidden bg-ivory-200">
                      <img
                        src={category.image || 'https://placehold.co/400x400/f5f3f0/9a8b7d?text=Awards'}
                        alt={category.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.src = 'https://placehold.co/400x400/f5f3f0/9a8b7d?text=Awards'; }}
                      />
                    </div>
                    <CardContent className="p-6">
                      <h3 className="font-heading font-semibold text-xl text-ivory-800 mb-2 group-hover:text-gold-700 transition-colors">
                        {category.name}
                      </h3>
                      <p className="text-ivory-500 text-sm mb-4 font-body">
                        {category.description || `Explore our ${category.name.toLowerCase()} collection`}
                      </p>
                      <span className="inline-flex items-center text-gold-600 font-medium text-sm font-body">
                        Shop Now ({category.count || 0} products) <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Products Section - Pearl gradient */}
      <section className="py-20 gradient-pearl">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-semibold text-ivory-800 mb-2">Featured Crystal Awards</h2>
              <p className="text-ivory-500 font-body">Our most popular recognition pieces</p>
            </div>
            <Button 
              variant="outline" 
              className="btn-luxury-outline hidden md:flex font-body"
              asChild
            >
              <Link to="/Awards/products">
                View All <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-10 h-10 text-gold-500 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/Awards/product/${product.id}`}
                  className="group"
                  data-testid={`featured-product-${product.id}`}
                >
                  <Card className="bg-ivory-50 border border-ivory-300/50 hover:border-gold-500/50 transition-all duration-300 overflow-hidden h-full card-luxury">
                    <div className="aspect-square overflow-hidden bg-ivory-100 p-4">
                      <img
                        src={product.image || 'https://placehold.co/300x300/f5f3f0/9a8b7d?text=Award'}
                        alt={product.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.src = 'https://placehold.co/300x300/f5f3f0/9a8b7d?text=Award'; }}
                      />
                    </div>
                    <CardContent className="p-5">
                      <h3 className="font-heading font-semibold text-lg text-ivory-800 mb-2 group-hover:text-gold-700 transition-colors line-clamp-1">
                        {product.name}
                      </h3>
                      <p className="text-ivory-500 text-sm mb-3 line-clamp-2 font-body">
                        {product.description}
                      </p>
                      <div className="text-gold-600 font-bold text-xl font-body">
                        {product.priceRange ? (
                          `$${product.priceRange.min?.toFixed(2) || '0.00'} - $${product.priceRange.max?.toFixed(2) || '0.00'}`
                        ) : (
                          `$${(product.basePrice || 0).toFixed(2)}`
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          <div className="mt-8 text-center md:hidden">
            <Button 
              variant="outline" 
              className="btn-luxury-outline font-body"
              asChild
            >
              <Link to="/Awards/products">
                View All Products <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-ivory-50">
        <div className="container mx-auto px-4">
          <Card className="gradient-champagne border border-gold-500/20 shadow-luxury-lg">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-heading font-semibold text-ivory-800 mb-4">
                Need a Custom Award?
              </h2>
              <p className="text-ivory-600 max-w-2xl mx-auto mb-8 font-body">
                We specialize in creating unique, custom-designed crystal awards for your special occasions. 
                Contact us for personalized quotes on bulk orders and custom designs.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button 
                  size="lg" 
                  className="btn-luxury-primary font-body"
                  onClick={() => setShowQuickOrder(true)}
                >
                  <Upload className="mr-2 w-5 h-5" />
                  Request Custom Quote
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-gold-500/50 text-gold-700 hover:bg-gold-500/10 font-body"
                >
                  Call: +1 800 521 0527
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Quick Order Modal */}
      <QuickOrderModal isOpen={showQuickOrder} onClose={() => setShowQuickOrder(false)} />
    </div>
  );
};

export default AwardsHome;
